<template>
  <div v-if="isOpen" class="filter-modal-container">
    <div class="filter-modal" @click.stop>
      <div class="filter-modal-header">
        <h2>{{ modalTitle }}</h2>
        
      </div>
      <div class="filter-modal-body">
        <div class="search-container">
          <SearchInput
              v-model="searchQuery"
              placeholder="Поиск"
              width="100%"
              height="36px"
          />
        </div>
        <div class="recipients-list">
          <div v-for="(recipient, index) in sortedFilteredRecipients" :key="index" class="recipient-item" :class="{'selected': isSelected(recipient)}"
                @click="(event) => selectRecipient(recipient, event)">
            {{ recipient.label }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, computed, watch, onMounted, onUnmounted, defineProps, defineEmits} from 'vue';
import SearchInput from "@/components/SearchInput.vue";

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  },
  title: {
    type: Object,
    default: () => ({title: '', buttonId: undefined})
  },
  recipients: {
    type: Array,
    default: () => []
  },
  selectedValue: {
    type: [String, Number, Object, Array],
    default: null
  },
    multiple: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['close', 'select']);
const modalTitle = computed(() => props.title.title || '');
const buttonId = computed(() => props.title.buttonId);

const searchQuery = ref('');

const selectedItems = ref([]);
// Проверка выбран ли элемент
const isSelected = (recipient) => {
  if (props.multiple) {
    return selectedItems.value.some(val => 
      val === recipient.value || 
      (typeof val === 'object' && val?.value === recipient.value)
    );
  }
  
  if (Array.isArray(props.selectedValue)) {
    return props.selectedValue.some(val => val === recipient.value || val === recipient);
  }
  return props.selectedValue === recipient.value || props.selectedValue === recipient;
};

const filteredRecipients = computed(() => {
  return props.recipients.filter(recipient =>
      recipient.label.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});
const sortedFilteredRecipients = computed(() => {
  return [...filteredRecipients.value].sort((a, b) => 
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
  );
});

const close = () => {
  // При закрытии модального окна, если это множественный выбор,
  // применяем текущий выбор
  if (props.multiple && selectedItems.value.length > 0) {
    applyMultipleSelection();
  }
  emit('close');
};
const selectRecipient = (recipient, event) => {
  // Stop event propagation to prevent handleClickOutside from being triggered
  if (event) {
    event.stopPropagation();
  }
  
  if (props.multiple) {
    const index = selectedItems.value.findIndex(item => 
      item === recipient.value || 
      (typeof item === 'object' && item?.value === recipient.value)
    );
    
    if (index === -1) {
      // Добавляем элемент, если его нет в списке
      selectedItems.value.push(recipient.value);
    } else {
      // Удаляем элемент, если он уже в списке
      selectedItems.value.splice(index, 1);
    }
    
    // Применяем выбор при каждом клике для обновления UI
    applyMultipleSelection();
    // НЕ закрываем модальное окно для множественного выбора
  } else {
    // Для одиночного выбора сразу закрываем модальное окно
    emit('select', recipient, buttonId.value);
    close();
  }
};
const applyMultipleSelection = () => {
  // Создаем объект с выбранными значениями и их метками
  const selectedRecipients = selectedItems.value.map(value => {
    const recipient = props.recipients.find(r => r.value === value || 
      (typeof value === 'object' && value?.value === r.value));
    return recipient || { value, label: value.toString() };
  });
  
  emit('select', { 
    value: selectedItems.value, 
    label: selectedRecipients.map(r => r.label).join(', ') 
  }, buttonId.value);
};


const handleClickOutside = (event) => {
  if (props.isOpen) {
    const modalElement = document.querySelector('.filter-modal-container');
    if (modalElement && !modalElement.contains(event.target)) {
      // При клике вне модального окна закрываем его
      close();
    }
  }
};

watch(() => props.isOpen, (newValue) => {
  if (newValue) {
    if (props.multiple) {
      // Если передан массив, копируем его, иначе создаем пустой массив или массив с одним элементом
      selectedItems.value = Array.isArray(props.selectedValue) 
        ? [...props.selectedValue] 
        : props.selectedValue ? [props.selectedValue] : [];
    } else {
      // Для одиночного выбора просто запоминаем текущее значение
      selectedItems.value = props.selectedValue ? [props.selectedValue] : [];
    }
    
    setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);
  } else {
    document.removeEventListener('click', handleClickOutside);
  }
});

onMounted(() => {
  if (props.isOpen) {
    document.addEventListener('click', handleClickOutside);
  }
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

</script>


<style scoped lang="scss">
.filter-modal {
  width: 100%;
  height: 100%;
  border-radius: 12px;
  padding: 8px;
  background-color: #fff;
  display: flex;
  flex-direction: column;
}

.filter-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  margin-bottom: 4px;

  h2 {
    line-height: 25px;
    font-size: 18px;
    font-weight: 600;
    font-family: 'Open Sans', sans-serif;
    color: #4D5460;
  }
}

.filter-modal-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.search-container {
  margin-bottom: 4px;
}

.recipients-list {
  flex: 1;
  overflow-y: auto;
  padding: 0;
}

.recipient-item {
  padding: 6px 8px;
  cursor: pointer;
  height: 31px;
  color: #4D5460;
  font-size: 14px;
  font-family: 'Open Sans', sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &.selected {
    background-color: #f6f6f6;
    color: #005291;
    font-weight: 600;
    border-radius: 4px;
  }

  &:hover {
    background-color: #f6f6f6;
    color: #005291;
    font-weight: 600;
    border-radius: 4px;
  }
}
</style>